// CurrentSensorLogs.js (alphatech)
// Daily & monthly consumption logs. Same endpoints as Aquabrim's TotalUnitLog
// (api/current-sensor-logs/daily & /monthly), presented as clean slate tables.
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../../../config/config';
import { SLATE, PHASE, SectionCard, PillTabs } from './sensorUi';

const num = (v) => (v === null || v === undefined || Number.isNaN(Number(v)) ? '—' : Number(v).toFixed(2));

const th = {
  textAlign: 'left',
  fontSize: '0.62rem',
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: SLATE.faint,
  fontWeight: 700,
  padding: '8px 12px',
  borderBottom: `1px solid ${SLATE.line}`,
  position: 'sticky',
  top: 0,
  background: SLATE.card,
};
const td = { padding: '8px 12px', fontSize: '0.8rem', color: SLATE.ink2, borderBottom: `1px solid ${SLATE.line2}`, fontVariantNumeric: 'tabular-nums' };

const CurrentSensorLogs = ({ sensor }) => {
  const deviceCode = sensor?.device_code;
  const subId = sensor?.sensor_sub_id;

  const [tab, setTab] = useState('daily');
  const [month, setMonth] = useState(dayjs().format('MMM-YYYY'));
  const [year, setYear] = useState(dayjs().year());
  const [daily, setDaily] = useState([]);
  const [monthly, setMonthly] = useState([]);

  // Month options: last 12 months. Year options: last 5 years.
  const monthOptions = useMemo(
    () => Array.from({ length: 12 }, (_, i) => dayjs().subtract(i, 'month').format('MMM-YYYY')),
    [],
  );
  const yearOptions = useMemo(() => {
    const y = dayjs().year();
    return Array.from({ length: 5 }, (_, i) => y - i);
  }, []);

  useEffect(() => {
    if (!deviceCode || !subId) return undefined;
    let cancelled = false;

    const fetchDaily = async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}api/current-sensor-logs/daily`, {
          params: { deviceCode, subId, month },
        });
        if (!cancelled && data?.success) setDaily(data.dailyLogs || []);
      } catch (_) { /* keep */ }
    };
    const fetchMonthly = async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}api/current-sensor-logs/monthly`, {
          params: { deviceCode, subId, year },
        });
        if (!cancelled && data?.success) setMonthly(data.monthlyLogs || []);
      } catch (_) { /* keep */ }
    };

    const run = () => { fetchDaily(); fetchMonthly(); };
    run();
    const id = setInterval(run, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [deviceCode, subId, month, year]);

  const selectStyle = {
    border: `1px solid ${SLATE.line}`,
    borderRadius: 8,
    padding: '5px 10px',
    fontSize: '0.76rem',
    fontWeight: 600,
    color: SLATE.ink,
    background: SLATE.card,
  };

  const rows = tab === 'daily' ? daily : monthly;

  return (
    <SectionCard
      icon={<span style={{ fontWeight: 900, color: PHASE.reactive }}>Σ</span>}
      title="Consumption Logs"
      subtitle="Apparent energy (kVAh)"
      right={
        <PillTabs
          value={tab}
          onChange={setTab}
          options={[{ value: 'daily', label: 'Daily' }, { value: 'monthly', label: 'Monthly' }]}
        />
      }
    >
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
        {tab === 'daily' ? (
          <select style={selectStyle} value={month} onChange={(e) => setMonth(e.target.value)}>
            {monthOptions.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        ) : (
          <select style={selectStyle} value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
      </div>

      <div style={{ maxHeight: 320, overflow: 'auto', border: `1px solid ${SLATE.line2}`, borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>{tab === 'daily' ? 'Date' : 'Month'}</th>
              <th style={{ ...th, textAlign: 'right' }}>Consumed (kVAh)</th>
              <th style={{ ...th, textAlign: 'right' }}>Totalizer (kVAh)</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={3} style={{ ...td, textAlign: 'center', color: SLATE.faint, padding: '24px 12px' }}>
                  No records for this {tab === 'daily' ? 'month' : 'year'}.
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={i}>
                  <td style={{ ...td, fontWeight: 700, color: SLATE.ink }}>
                    {tab === 'daily' ? (r.fullDate || r.date) : r.month}
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>{num(r.consumption)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{num(r.totalizerApparent)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
};

export default CurrentSensorLogs;
